import { CommonModule } from '@angular/common';
import { AgGridModule } from '@ag-grid-community/angular';
import { RowClickedEvent } from '@ag-grid-community/core';
import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { AccessManagementOrgUnitCollectionRecord } from '@cdw-ae/common-data';
import { MatDialog } from '@cdw-ae/common-frontend-materials';
import { DrawerContent } from '@cdw-ae/common-ui-components-drawer-content';
import {
  CdwColDef,
  CdwSearchGridComponent,
  CdwSearchGridModule,
} from '@cdw-ae/common-ui-components-search-grid';
import { CdwConfirmationDialogComponent, CdwDialogModule, ConfirmationDialogResult } from '@cdw-ae/common-ui-components-dialog';
import { ErrorSnackbarComponent } from '@cdw-ae/components-error-snackbar';
import { FilterBarField } from '@cdw-ae/filter-bar-interfaces';
import { FilterBarTextMatchersModule } from '@cdw-ae/filter-bar-text-matchers';
import { take } from 'rxjs';
import { OuCollectionsFilterSettings } from '../../../../../../common/services/route-config/configs/filter-bar-config.config';
import { AccessManagementFrontendAuthService } from '../access-management-frontend-auth.service';
import { TailwindColors } from '../../../../../../common/constants/tailwind-colors';
import { OrgUnitCollectionsDataService } from './ou-collections-data.service';
import { EmptyStateOverlayComponent } from '../../../../../../components/empty-state-overlay/empty-state-overlay.component';

@Component({
  selector: 'ou-collections',
  standalone: true,
  imports: [
    CommonModule,
    AgGridModule,
    MatProgressSpinnerModule,
    FormsModule,
    ReactiveFormsModule,
    CdwSearchGridModule,
    CdwDialogModule,
    FilterBarTextMatchersModule,
    EmptyStateOverlayComponent,
  ],
  templateUrl: './ou-collections.component.html',
  styleUrl: './ou-collections.component.scss',
})
export class OuCollectionsComponent implements OnInit {
  @ViewChild('ouCollectionsGrid')
  public ouCollectionsGrid!: CdwSearchGridComponent<AccessManagementOrgUnitCollectionRecord>;
  public drawerContent: DrawerContent[] = [];
  public drawerContentTitle = '';
  public filterBarSettings!: FilterBarField[];
  public orgUnitCollectionsSearchComponentId = 'orgUnitCollectionsComponent';
  public readonly searchFieldMask: string[] = ['orgUnit.value', 'name', 'description'];
  public filterBySelectedColumnName = 'id';

  @Output() updateDrawerContent =
    new EventEmitter<AccessManagementOrgUnitCollectionRecord>();

  public columnDefs: CdwColDef[] = [
    {
      field: 'name',
      headerName: 'OU Collection',
      width: 300,
      sort: 'asc',
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 600,
    },
    {
      cellRenderer: 'deleteItemIconComponent',
      headerName: 'Delete',
      flex: 1,
    },
  ];

  public itemLabel = 'org unit collection';

  // Theme colors for search grid
  public customTextColor: string = TailwindColors.gray300;
  public firstColumnTextColor: string = TailwindColors.tertiaryBlueLink;

  public isLoading = false;

  // Show empty state overlay for testing
  showEmptyState = false;

  // HTML content for empty state overlay
  public ouCollectionsEmptyStateHtml = this.getEmptyStateHtml();

  constructor(
    public orgUnitCollectionDataService: OrgUnitCollectionsDataService,
    private accessManagementAuthService: AccessManagementFrontendAuthService,
    private snackbar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router
  ) {
    this.filterBarSettings = OuCollectionsFilterSettings.filter(
      (filterBarField: FilterBarField): boolean =>
        this.searchFieldMask.includes(filterBarField.name)
    );
  }

  async ngOnInit(): Promise<void> {
    this.isLoading = true;
    await this.accessManagementAuthService.setUserAuthContext();
  }

  onDataLoaded(): void {
    this.isLoading = false;
  }

  public onRowClicked(event: RowClickedEvent): void {
    this.emitUpdateDrawerContent(event);
  }

  deleteOuCollectionClicked(event: {
    rowId: number | string;
    gridComponentId?: string;
  }) {
    const id = (event as unknown as { rowId: number }).rowId;
    const customerId =
      this.accessManagementAuthService.authContext?.sqlCustomerId ?? null;
    if (!customerId) {
      this.snackbar.openFromComponent(ErrorSnackbarComponent, {
        data: 'Authentication context is not available.',
      });
      return;
    }

    // Check if OU collection can be deleted before showing confirmation dialog
    this.orgUnitCollectionDataService.canDeleteOrgUnitCollection(id)
      .pipe(take(1))
      .subscribe((canDeleteResponse) => {
        if (!canDeleteResponse.canDelete) {
          // Show error immediately without confirmation dialog
          this.snackbar.openFromComponent(ErrorSnackbarComponent, {
            data: canDeleteResponse.message || 'Error deleting OU collection, it is assigned to a designation',
          });
          return;
        }

        // OU collection can be deleted, show confirmation dialog
        const deleteDialogRef = this.dialog.open(CdwConfirmationDialogComponent, {
          data: {
            title: 'Confirmation',
            message: 'Are you sure you want to delete this OU collection?',
            type: 'warning',
            layout: 'warning-box',
            confirmText: 'Delete',
            cancelText: 'Cancel',
            confirmButtonColor: 'warn',
            hard: true,
          },
          width: '540px',
          maxWidth: '100%',
          disableClose: true,
          hasBackdrop: true,
        });

        deleteDialogRef
          .afterClosed()
          .pipe(take(1))
          .subscribe(async (result: ConfirmationDialogResult | undefined) => {
            if (result?.confirmed && id) {
              const observable =
                await this.orgUnitCollectionDataService.deleteOrgUnitCollection(id);
              observable
                .pipe(take(1))
                .subscribe((response: { success: boolean }) => {
                  if (response.success) {
                    this.snackbar.open('OU Collection deleted successfully.', 'OK', { duration: 6000 });
                    this.refreshSearchGrid();
                  } else {
                    this.snackbar.openFromComponent(ErrorSnackbarComponent, {
                      data: 'Error deleting OU collection, it is assigned to a designation',
                    });
                  }
                });
            }
          });
      });
  }

  emitUpdateDrawerContent(event: {
    data: AccessManagementOrgUnitCollectionRecord;
  }) {
    this.updateDrawerContent.emit(event.data);
  }

  refreshSearchGrid() {
    this.ouCollectionsGrid.refreshGrid();
  }

  onAddNewOUCollection(): void {
    this.router.navigate(['administration/access-management/org-unit-collection/edit/new']);
  }

  getEmptyStateHtml(): string {
    return `No OU collections have been set up. Start by <a href="/administration/access-management/org-unit-collection/edit/new" class="empty-state-link">adding a new OU collection</a>.`;
  }
}
